/**
 * Plain-Italian one-line summaries for page-builder actions (ADR-0044 U2 —
 * Appsmith-grade readable action editors).
 *
 * The action/row-action editors render cramped, unlabelled controls ("Open ·
 * Deal · Drawer") that read as gibberish at a glance. These PURE helpers turn an
 * action into a human sentence ("Apri «Deal» in drawer") shown as a row header,
 * so a chain reads top-to-bottom before you expand any field. No React, no i18n
 * singleton — kept here so it's unit-testable and shared by both editors.
 */
import type { PageAction } from "./pageActions";

/** A row-action on a `data_list` (mirrors `RowActionsEditor.RowAction`). */
export interface RowActionLike {
  label?: string;
  action?: "open" | "edit" | "delete" | "select";
  target_page?: string;
  open_mode?: "page" | "drawer";
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
/** Quote a value, or fall back to a muted placeholder when it's empty. */
const q = (v: unknown, empty = "…"): string => {
  const s = str(v);
  return s ? `«${s}»` : empty;
};

/** Human title for a page slug given the app's page list (slug → title). */
function pageTitle(slug: string, pages: { slug: string; title?: string }[]): string {
  const hit = pages.find((p) => p.slug === slug);
  return (hit?.title || hit?.slug || slug || "").trim();
}

/**
 * One-line Italian summary of an action-chain action. Falls back to the bare
 * type for an unknown action so it never throws on malformed AST.
 */
export function summarizeAction(
  action: PageAction,
  pages: { slug: string; title?: string }[] = [],
): string {
  const a = action as Record<string, unknown>;
  switch (action.type) {
    case "set_variable":
      return `Imposta la variabile ${q(a.name)}`;
    case "navigate":
      return `Vai a ${q(a.to)}`;
    case "open_drawer":
      return `Apri la pagina ${q(pageTitle(str(a.page), pages) || str(a.page))} in drawer`;
    case "open_overlay": {
      const mode = a.mode === "modal" ? "modale" : "drawer";
      return `Apri ${q(pageTitle(str(a.page), pages) || str(a.page))} (${mode})`;
    }
    case "close_overlay":
      return "Chiudi l'overlay (restituisci il risultato)";
    case "refresh":
      return "Ricarica le query";
    case "toast":
      return `Mostra un messaggio: ${q(a.message)}`;
    case "create_row":
      return `Crea una riga in ${q(a.table)}`;
    case "update_row":
      return `Aggiorna una riga in ${q(a.table)}`;
    case "delete_row":
      return `Elimina una riga in ${q(a.table)}`;
    case "run_workflow":
      return `Esegui il workflow ${q(a.workflow)}`;
    case "run_query":
      return `Esegui la query ${q(a.query)}`;
    case "run_js":
      return `Esegui la funzione JS ${q(a.fn)}`;
    case "global_value":
      return `Salva nello store ${q(a.key)}`;
    case "global_remove":
      return `Rimuovi dallo store ${q(a.key)}`;
    case "global_clear":
      return "Svuota lo store";
    case "if":
      return `Se ${q(a.condition, "(condizione)")} …`;
    case "try":
      return "Prova / in caso di errore …";
    default:
      return str(action.type) || "Azione";
  }
}

/** Italian verb for a row-action kind. */
const ROW_ACTION_VERB: Record<NonNullable<RowActionLike["action"]>, string> = {
  open: "Apri",
  edit: "Modifica",
  delete: "Elimina",
  select: "Seleziona",
};

/**
 * One-line Italian summary of a `data_list` row-action. `open`/`edit` target a
 * page (drawer or full page); `delete`/`select` act on the row in place.
 */
export function summarizeRowAction(
  r: RowActionLike,
  pages: { slug: string; title?: string }[] = [],
): string {
  const kind = r.action ?? "open";
  const verb = ROW_ACTION_VERB[kind];
  if (kind === "delete") return "Elimina la riga";
  if (kind === "select") return "Seleziona la riga";
  // open / edit → go to a page
  const target = pageTitle(str(r.target_page), pages) || str(r.target_page);
  const where = r.open_mode === "page" ? "in una pagina" : "in drawer";
  return target ? `${verb} ${q(target)} ${where}` : `${verb} la riga ${where}`;
}
